import { DndContext, type DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { Empty, Flex, Form, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useCallback } from "react"

import type {
  DocumentAvailableBlock,
  DocumentTemplateBlock,
  DocumentTemplateMutateData
} from "../../../types/types.ts"
import { TemplateFormSortableBlockItem } from "./TemplateFormSortableBlockItem.tsx"

interface ProposalTemplatesBuilderBodyProps {
  form: FormInstance<DocumentTemplateMutateData>
  blocksLookup: Map<string, DocumentAvailableBlock>
}

export function TemplateFormFirstStep({
  form,
  blocksLookup
}: ProposalTemplatesBuilderBodyProps) {
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) return

      const blocks: DocumentTemplateBlock[] = form.getFieldValue("blocks") || []
      const activeIndex = blocks.findIndex((b) => b.id === active.id)
      const overIndex = blocks.findIndex((b) => b.id === over.id)

      if (activeIndex === -1 || overIndex === -1) return

      const reordered = arrayMove(blocks, activeIndex, overIndex).map(
        (block, index) => ({ ...block, order: index })
      )

      form.setFieldValue("blocks", reordered)
    },
    [form]
  )

  return (
    <Form.List
      name="blocks"
      rules={[
        {
          validator: async (_, blocks: DocumentTemplateBlock[]) => {
            if (!blocks?.length) {
              return Promise.reject(new Error("Add at least one block"))
            }
            return Promise.resolve()
          }
        }
      ]}
    >
      {(fields, { remove }, { errors }) => {
        const blocks: DocumentTemplateBlock[] =
          form.getFieldValue("blocks") || []
        const ids = blocks.map((b) => b.id)

        return (
          <Flex gap={"middle"} className={"overflow-hidden"} flex={1} vertical>
            <Typography.Text type="secondary">
              {fields.length} {fields.length === 1 ? "block" : "blocks"}
            </Typography.Text>

            <div className={"overflow-auto tailwind-scroll flex-1"}>
              {fields.length === 0 ? (
                <Empty
                  description={
                    errors?.length ? (
                      <Form.ErrorList
                        className={"text-error"}
                        errors={errors}
                      />
                    ) : (
                      "Click blocks from the sidebar to add them to your template"
                    )
                  }
                />
              ) : (
                <DndContext
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={ids}
                    strategy={verticalListSortingStrategy}
                  >
                    <Flex vertical gap={8}>
                      {fields.map((field) => {
                        const block = blocks[field.name]
                        if (!block) return null

                        return (
                          <TemplateFormSortableBlockItem
                            key={block.id}
                            block={block}
                            fieldName={field.name}
                            availableBlock={blocksLookup.get(block.block_type)}
                            onRemove={() => remove(field.name)}
                          />
                        )
                      })}
                    </Flex>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </Flex>
        )
      }}
    </Form.List>
  )
}
